import { type ComponentProps, Fragment } from "react";
import { Badge, Box, Column, Row, Text } from "@hightouch/htui";

import { HeadingWithTooltip } from "src/components/heading-with-tooltip";
import { MediaDetailHeaderControls } from "src/components/media-detail/media-detail-header-controls";

type Status = {
  label: string;
  variant?: NonNullable<ComponentProps<typeof Badge>["variant"]>;
};

type Props = {
  title: string;
  /** Short facts joined with a middle dot, e.g. format and parent ad set. */
  metaTags?: readonly string[];
  /** Badges such as delivery status and fatigue. */
  statuses?: readonly Status[];
  /** Short notes that sit beside the badges, e.g. days active. */
  notes?: readonly string[];
};

/**
 * Title row of a detail view, with the prev/next and close controls from the
 * surrounding `MediaDetailControlsProvider`. Use `MediaDetailHeaderSkeleton`
 * while the data loads.
 */
export function MediaDetailHeader({
  title,
  metaTags = [],
  statuses = [],
  notes = [],
}: Props) {
  return (
    <Column gap={2} minW={0}>
      <Row align="center" justify="space-between" gap={4} minW={0}>
        <Box minW={0} flex={1}>
          <HeadingWithTooltip size="md">{title}</HeadingWithTooltip>
        </Box>
        <MediaDetailHeaderControls />
      </Row>
      {metaTags.length > 0 && (
        <Row align="center" gap={1} minW={0} flexWrap="wrap">
          {metaTags.map((tag, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: a fixed positional list whose text can repeat
            <Fragment key={index}>
              {index > 0 && (
                <Text size="sm" color="text.secondary" aria-hidden>
                  ·
                </Text>
              )}
              <Text size="sm" color="text.secondary">
                {tag}
              </Text>
            </Fragment>
          ))}
        </Row>
      )}
      {(statuses.length > 0 || notes.length > 0) && (
        <Row align="center" gap={2} flexWrap="wrap">
          {statuses.map((status, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: a fixed positional list whose labels can repeat
            <Badge key={index} variant={status.variant} size="sm">
              {status.label}
            </Badge>
          ))}
          {notes.map((note, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: a fixed positional list whose text can repeat
            <Text key={index} size="sm" color="text.tertiary">
              {note}
            </Text>
          ))}
        </Row>
      )}
    </Column>
  );
}
